import { useEffect, useMemo } from 'react';

import { useApi } from './use-api';
import { useAssociations, useTasks, useWorkOrders } from './use-data';
import { useFeatureFlags } from './use-feature-flags';
import { useTodoItems } from './use-todo-items';

import type { Task, WorkOrder } from '@/types/models';
import { businessDayOf, dueDayMs, parseDateToMs } from '@/utils/date-utils';
import { toPlainTextPreview } from '@/utils/plain-text-preview';
import { rankUpNext, type UpNextItem } from '@/utils/up-next';

const WORK_ORDER_QUERY = {
	status: 'open,in_progress', limit: 500, sortBy: 'due_date', sortDirection: 'asc',
} as const;

/**
 * The maintenance "Up Next" list: what the viewer should start on, in the order to start it.
 *
 * Four sources — open work orders assigned to the viewer, open work orders nobody owns, and
 * the viewer's follow-ups and tasks — merged into one list and ranked by `rankUpNext`.
 * Follow-ups come from `useTodoItems` so the two panels cannot disagree about which exist.
 * Tasks are fetched here: the To Do list drops undated tasks and anything past its 14-day
 * horizon, which would hide them from Up Next too. The full ranked list is returned; the
 * panel decides how many to show.
 */
export const useUpNext = (): { items: UpNextItem[]; isLoading: boolean } => {
	const { user } = useApi();
	const userId = user?.id;

	/**
	 * Memoised: a fresh object per render is a fresh TanStack key and therefore a refetch loop.
	 *
	 * The server orders by priority tier first, then `sortBy`, so earliest-due within each tier
	 * matches `rankUpNext`. At the 500-row server max the page is the whole open set at any
	 * realistic volume; past it, SQLite's ASC puts NULL due dates first within a tier, so
	 * undated rows (which rank last) could still crowd out dated ones.
	 */
	const mineQuery = useMemo(() => ({
		assignedTo: userId, ...WORK_ORDER_QUERY, enabled: !!userId,
	}), [userId]);
	const unassignedQuery = useMemo(() => ({
		assignedTo: 'unassigned', ...WORK_ORDER_QUERY, enabled: !!userId,
	}), [userId]);
	// Gated exactly like `useTodoItems`: without the flag the tasks API answers 403.
	const { hasFlag } = useFeatureFlags();
	const tasksEnabled = hasFlag('tasks_enabled') && !!userId;
	const tasksQuery = useMemo(() => ({
		assignedTo: userId, status: 'open,in_progress', limit: 500, enabled: tasksEnabled,
	}), [userId, tasksEnabled]);
	const tasks = useTasks(tasksQuery);
	const mine = useWorkOrders(mineQuery);
	const unassigned = useWorkOrders(unassignedQuery);
	// Served from the cache the sidebar and To Do panel already fill.
	const {
		data: associationsData, isError: associationsFailed, isSuccess: associationsLoaded, refetch: refetchAssociations,
	} = useAssociations({ status: 'active', limit: 1000 });
	/**
	 * Self-heal the association names after an API blip.
	 *
	 * `dataService.getAssociations` swallows a network error and resolves an EMPTY page, which
	 * TanStack then caches as a fresh success for five minutes; associations are not a live key
	 * either, so the next live refresh restores the work orders but not the names, and every
	 * title drops to a bare "#31347". An org with open work orders always has associations, so
	 * an empty (or failed) list beside them means the fetch failed: retry it whenever the work
	 * orders come back. Keyed on the fetch TIME, because an unchanged refetch keeps the same
	 * `data` object (structural sharing) and would never re-run this.
	 */
	const associationCount = (associationsData as { data?: unknown[] } | undefined)?.data?.length ?? 0;
	const workOrderCount = ((mine.data as { data?: unknown[] } | undefined)?.data?.length ?? 0)
		+ ((unassigned.data as { data?: unknown[] } | undefined)?.data?.length ?? 0);
	const namesMissing = workOrderCount > 0
		&& (associationsFailed || (associationsLoaded && associationCount === 0));
	useEffect(() => {
		if (namesMissing) void refetchAssociations();
	}, [namesMissing, refetchAssociations, mine.dataUpdatedAt, unassigned.dataUpdatedAt]);
	const todo = useTodoItems();

	const items = useMemo<UpNextItem[]>(() => {
		const associationName = new Map<string, string>(
			((associationsData as { data?: Array<{ id: string; name: string }> } | undefined)?.data ?? [])
				.map((a) => [a.id, a.name]),
		);
		const mineRows = ((mine.data as { data?: WorkOrder[] } | undefined)?.data ?? []);
		const unassignedRows = ((unassigned.data as { data?: WorkOrder[] } | undefined)?.data ?? []);

		// Mine first, so a work order in both sets keeps `mine: true`.
		const workOrders = new Map<string, { wo: WorkOrder; mine: boolean }>();
		for (const wo of mineRows) workOrders.set(wo.id, { wo, mine: true });
		for (const wo of unassignedRows) {
			if (!workOrders.has(wo.id)) workOrders.set(wo.id, { wo, mine: false });
		}

		const dueOf = (wo: WorkOrder): number | null => {
			const ms = parseDateToMs(wo.dueDate);
			return ms === null ? null : businessDayOf(ms);
		};

		const out: UpNextItem[] = [];
		for (const { wo, mine: isMine } of workOrders.values()) {
			const number = wo.orderNumber ? `#${wo.orderNumber}` : wo.id.substring(0, 8);
			const name = associationName.get(wo.associationId);
			out.push({
				key: `work-order:${wo.id}`,
				kind: 'work-order',
				priority: wo.priority,
				dueMs: dueOf(wo),
				mine: isMine,
				href: `/work-orders/${wo.id}`,
				title: name ? `${number} — ${name}` : number,
				subtitle: wo.jobLocation || undefined,
				preview: toPlainTextPreview(wo.workRequested),
			});
		}

		const followUpTaskIds = new Set<string>();
		for (const t of todo.items) {
			if (t.kind === 'follow-up' && t.workOrderId) {
				if (t.taskId) followUpTaskIds.add(t.taskId);
				// Sorts exactly like its work order when we have it; otherwise its own (undated) self.
				const parent = workOrders.get(t.workOrderId);
				out.push({
					key: t.id,
					kind: 'follow-up',
					priority: parent ? parent.wo.priority : null,
					dueMs: parent ? dueOf(parent.wo) : t.dueMs,
					mine: parent ? parent.mine : true,
					href: `/work-orders/${t.workOrderId}`,
					title: t.title,
					subtitle: t.subtitle ?? t.associationName,
					preview: parent ? toPlainTextPreview(parent.wo.workRequested) : undefined,
				});
			}
		}

		// A follow-up is also a task row (`task_list = 'follow_up'`); it is listed as its follow-up.
		const seenTasks = new Set<string>();
		for (const t of ((tasks.data as { data?: Task[] } | undefined)?.data ?? [])) {
			if (t.taskList === 'follow_up' || followUpTaskIds.has(t.id) || seenTasks.has(t.id)) continue;
			seenTasks.add(t.id);
			out.push({
				key: `task:${t.id}`,
				kind: 'task',
				priority: null,
				dueMs: dueDayMs(t.dueDate),
				mine: true,
				href: `/tasks/${t.id}`,
				title: t.title,
				subtitle: t.associationId ? associationName.get(t.associationId) : undefined,
				preview: toPlainTextPreview(t.description),
			});
		}

		return rankUpNext(out);
	}, [mine.data, unassigned.data, associationsData, todo.items, tasks.data]);

	return {
		items,
		isLoading: mine.isLoading || unassigned.isLoading || todo.isLoading || tasks.isLoading,
	};
};
